import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { resolveMediaUrl } from '../utils/media';
import { getConfidenceLevel } from '../utils/confidence';
import { getLaymenAuthorLabel } from '../utils/laymenAlias';

const normalizeSuggestionPayload = (payload) => {
    if (!payload || typeof payload !== 'object') {
        return { byTags: [], byAuthor: [] };
    }

    return {
        byTags: Array.isArray(payload.byTags) ? payload.byTags : [],
        byAuthor: Array.isArray(payload.byAuthor) ? payload.byAuthor : [],
    };
};

const SuggestedPostCard = ({ post, onOpen }) => {
    const thumbnailSrc = resolveMediaUrl(post?.thumbnail);
    const confidenceValue = Number(post?.confidenceRating ?? post?.confidenceScoreRating);
    const { label: confidenceLabel, tone: confidenceTone } = getConfidenceLevel(confidenceValue);
    const authorLabel = getLaymenAuthorLabel(post);

    return (
        <article className="suggestion-card">
            <button
                type="button"
                className="suggestion-card__button"
                onClick={() => onOpen?.(post)}
            >
                {thumbnailSrc && (
                    <div className="suggestion-card__thumbnail">
                        <img src={thumbnailSrc} alt="" loading="lazy" />
                    </div>
                )}
                <div className="suggestion-card__body">
                    <div className="suggestion-card__meta">
                        <span className="suggestion-card__date">{post?.date}</span>
                        <span className={`suggestion-card__confidence suggestion-card__confidence--${confidenceTone}`}>
                            {confidenceLabel}
                        </span>
                    </div>
                    <h5 className="suggestion-card__title">{post?.title}</h5>
                    <div className="suggestion-card__author">{authorLabel}</div>
                    <p className="suggestion-card__description">{post?.description}</p>
                </div>
            </button>
        </article>
    );
};

const PostSuggestions = ({ post, onSelectPostId }) => {
    const postId = post?._id;
    const [suggestions, setSuggestions] = useState(() => ({ byTags: [], byAuthor: [] }));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!postId) {
            setSuggestions({ byTags: [], byAuthor: [] });
            setLoading(false);
            setError('');
            return undefined;
        }

        let isActive = true;

        const fetchSuggestions = async () => {
            setLoading(true);
            setError('');
            setSuggestions({ byTags: [], byAuthor: [] });

            try {
                const response = await axios.get(`${API_URL}/api/posts/${postId}/suggestions`);
                if (!isActive) {
                    return;
                }
                setSuggestions(normalizeSuggestionPayload(response.data));
            } catch (err) {
                if (!isActive) {
                    return;
                }
                const message = err?.response?.data?.message || err?.message || 'Failed to load suggestions';
                setError(message);
                setSuggestions({ byTags: [], byAuthor: [] });
            } finally {
                if (isActive) {
                    setLoading(false);
                }
            }
        };

        fetchSuggestions();

        return () => {
            isActive = false;
        };
    }, [postId]);

    const hasSuggestions = useMemo(() => (
        (Array.isArray(suggestions.byTags) && suggestions.byTags.length > 0)
        || (Array.isArray(suggestions.byAuthor) && suggestions.byAuthor.length > 0)
    ), [suggestions.byAuthor, suggestions.byTags]);

    const handleOpen = (nextPost) => {
        const nextId = nextPost?._id;
        if (!nextId) {
            return;
        }
        if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        }
        onSelectPostId?.(nextId);
    };

    if (!postId) {
        return null;
    }

    if (!loading && !hasSuggestions && !error) {
        return null;
    }

    return (
        <section className="post-suggestions" aria-label="Suggested articles">
            <h3 className="post-suggestions__title">Suggested Articles</h3>

            {error && <p className="post-suggestions__error">[SYSTEM ERROR]: {error}</p>}
            {loading && <p className="post-suggestions__loading">[GENERATING SUGGESTIONS...]</p>}

            {suggestions.byTags.length > 0 && (
                <div className="post-suggestions__section">
                    <h4 className="post-suggestions__section-title">Suggested By Topic</h4>
                    <div className="post-suggestions__grid">
                        {suggestions.byTags.slice(0, 3).map((entry) => (
                            <SuggestedPostCard
                                key={entry._id}
                                post={entry}
                                onOpen={handleOpen}
                            />
                        ))}
                    </div>
                </div>
            )}

            {suggestions.byAuthor.length > 0 && (
                <div className="post-suggestions__section">
                    <h4 className="post-suggestions__section-title">More From This Author</h4>
                    <div className="post-suggestions__grid">
                        {suggestions.byAuthor.slice(0, 3).map((entry) => (
                            <SuggestedPostCard
                                key={entry._id}
                                post={entry}
                                onOpen={handleOpen}
                            />
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
};

export default PostSuggestions;
